import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { configService, User } from "../services/configService";
import { Users, Plus, Trash2, Edit, Eye, EyeOff, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/dateUtils";
import { isTokenInvalidationError, isLoggingOut } from "../utils/errorUtils";

const emailRegex =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isStrongPassword = (value: string) => {
  const minLen = value.length >= 8;
  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasSpecial = /[^A-Za-z0-9]/.test(value);
  return minLen && hasUpper && hasLower && hasNumber && hasSpecial;
};

const isValidUsername = (value: string) =>
  /^[a-zA-Z0-9_]{3,20}$/.test(value);

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({
    username: "",
    name: "",
    email: "",
    password: "",
    role: "admin" as "admin" | "staff",
  });
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    role: "admin" as "admin" | "staff",
    isActive: true,
    newPassword: "",
    confirmPassword: "",
  });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const prevQueryRef = useRef(query);

  useEffect(() => {
    if (user?.role !== "superadmin") {
      navigate("/");
      return;
    }
    loadUsers(1);
  }, [user]);

  const loadUsers = async (pageNum: number, q?: string) => {
    try {
      setIsLoading(true);
      const res = await configService.getUsers(pageNum, perPage, q);
      setUsers(res.items);
      setTotalPages(res.total_pages);
    } catch (e) {
      if (!isTokenInvalidationError(e) && !isLoggingOut()) {
        toast.error("Failed to load users");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const didQueryChange = prevQueryRef.current !== query;
    prevQueryRef.current = query;
    if (didQueryChange && page !== 1) {
      setPage(1);
      return;
    }
    loadUsers(page, query);
  }, [page, query]);

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error("Please enter a value to search");
      return;
    }
    setPage(1);
    await loadUsers(1, query);
  };

  const clearSearch = async () => {
    setQuery("");
    setPage(1);
    await loadUsers(1, "");
  };

  const validateForm = () => {
    const next: Record<string, string> = {};

    if (!newUser.username.trim()) {
      next.username = "Username is required";
    } else if (!isValidUsername(newUser.username)) {
      next.username =
        "Username must be 3–20 characters (letters, numbers, underscores)";
    }

    if (!newUser.name.trim()) {
      next.name = "Name is required";
    }

    if (!newUser.email.trim()) {
      next.email = "Email is required";
    } else if (!emailRegex.test(newUser.email.trim())) {
      next.email = "Please enter a valid email address";
    }

    if (!newUser.password) {
      next.password = "Password is required";
    } else if (!isStrongPassword(newUser.password)) {
      next.password =
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };


  const handleCreateUser = async () => {
    try {
      if (!validateForm()) return;
      setIsSaving(true);
      await configService.createUser({
        username: newUser.username.trim(),
        email: newUser.email.trim(),
        password: newUser.password,
        role: newUser.role,
        name: newUser.name.trim(),
      });
      toast.success("User created successfully");
      setShowCreateUser(false);
      setNewUser({ username: "", name: "", email: "", password: "", role: "admin" });
      setPage(1);
      await loadUsers(1, query);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create user");
    } finally {
      setIsSaving(false);
    }
  };

  const openEdit = (u: User) => {
    setSelectedUser(u);
    setEditForm({
      name: u.name || "",
      role: (u.role as "admin" | "staff") || (u.is_admin ? "admin" : "staff"),
      isActive: u.is_active,
      newPassword: "",
      confirmPassword: "",
    });
    setEditErrors({});
    setShowEditUser(true);
  };

  const saveEdit = async () => {
    if (!selectedUser) return;

    const nextErrors: Record<string, string> = {};
    if (!editForm.name.trim()) nextErrors.name = "Name is required";

    if (editForm.newPassword || editForm.confirmPassword) {
      if (!editForm.newPassword) {
        nextErrors.newPassword = "New password is required";
      } else if (!isStrongPassword(editForm.newPassword)) {
        nextErrors.newPassword =
          "Password must be at least 8 characters and include uppercase, lowercase, number, and special character";
      }

      if (!editForm.confirmPassword) {
        nextErrors.confirmPassword = "Please confirm the password";
      } else if (editForm.newPassword !== editForm.confirmPassword) {
        nextErrors.confirmPassword = "Passwords do not match";
      }
    }


    if (Object.keys(nextErrors).length > 0) {
      setEditErrors(nextErrors);
      return;
    }
    setEditErrors({});

    try {
      setIsSaving(true);
      const originalUser = selectedUser;
      const roleChanged = originalUser?.role !== editForm.role;
      const statusChanged = originalUser?.is_active !== editForm.isActive;
      const passwordChanged = !!(editForm.newPassword || editForm.confirmPassword);
      
      await configService.updateUser(String(selectedUser.id), {
        name: editForm.name.trim(),
        role: editForm.role,
        isActive: editForm.isActive,
      });
      if (editForm.newPassword || editForm.confirmPassword) {
        await configService.resetUserPassword(String(selectedUser.id), {
          newPassword: editForm.newPassword,
          confirmPassword: editForm.confirmPassword,
        });
      }
      
      // Show appropriate success message
      const changes = [];
      if (roleChanged) changes.push("role");
      if (statusChanged) changes.push("status");
      if (passwordChanged) changes.push("password");
      
      if (changes.length > 0) {
        toast.success(`User updated successfully. ${changes.join(", ")} changed - user will be logged out on next request.`);
      } else {
        toast.success("User updated successfully");
      }
      
      setShowEditUser(false);
      setSelectedUser(null);
      await loadUsers(page, query);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update user");
    } finally {
      setIsSaving(false);
    }
  };

  const openDelete = (u: User) => {
    setSelectedUser(u);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;
    try {
      setIsSaving(true);
      await configService.deleteUser(String(selectedUser.id));
      toast.success("User deleted");
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      const nextPage = Math.min(page, Math.max(1, totalPages));
      setPage(nextPage);
      await loadUsers(nextPage, query);
    } catch (e) {
      if (!isTokenInvalidationError(e) && !isLoggingOut()) {
        toast.error(e instanceof Error ? e.message : "Failed to delete user");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <button
          onClick={() => setShowCreateUser(true)}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* {users.length === 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-10 sm:p-10 text-center">
            <Users className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">No users found</p>
            <div className="mt-4">
              <button
                onClick={() => setShowCreateUser(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Filters row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Search by username, email, name"
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="px-3 py-2 text-sm rounded-md border bg-white hover:bg-gray-50"
          >
            Search
          </button>
          <button
            onClick={clearSearch}
            className="px-3 py-2 text-sm rounded-md border bg-white hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Users
            </h3>
          </div>
          <div className="mt-2 overflow-x-auto ring-1 ring-gray-300 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                    #
                  </th>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Username
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Email
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Role
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Created
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-10 text-center">
                      <div className="flex items-center justify-center h-20">
                        <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
                      </div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                      <Users className="mx-auto h-10 w-10 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500">No users found</p>
                    </td>
                  </tr>
                ) : (
                  users.map((u, idx) => (
                    <tr key={u.id}>
                      <td className="py-4 pl-4 pr-3 text-sm sm:pl-6 text-gray-700">
                        {(page - 1) * perPage + idx + 1}
                      </td>
                      <td className="py-4 pl-4 pr-3 text-sm sm:pl-6">
                        <div className="text-sm font-medium text-gray-900">
                          {u.name || "-"}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900">
                        {u.username}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        {u.email}
                      </td>
                      <td className="px-3 py-4 text-sm">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          {(u.role || (u.is_admin ? "admin" : "staff"))?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-sm">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${u.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                            }`}
                        >
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        {u.created_at
                          ? formatDate(u.created_at)
                          : "N/A"}
                      </td>
                      <td className="px-3 py-4 text-sm font-medium">
                        <button className="text-primary-600 hover:text-primary-900 mr-3" onClick={() => openEdit(u)}>
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-900" onClick={() => openDelete(u)}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => {
                    const next = Math.max(1, page - 1);
                    setPage(next);
                    loadUsers(next);
                  }}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => {
                    const next = Math.min(totalPages, page + 1);
                    setPage(next);
                    loadUsers(next);
                  }}
                  disabled={page === totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{page}</span> of{" "}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => {
                        const next = Math.max(1, page - 1);
                        setPage(next);
                        loadUsers(next, query);
                      }}
                      disabled={page === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20"
                    >
                      <span className="sr-only">Previous</span>
                      {/* chevron left */}
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.78 15.22a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 010-1.06l4.5-4.5a.75.75 0 111.06 1.06L8.81 10l3.97 3.97a.75.75 0 010 1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {(() => {
                      const buttons: number[] = [];
                      const start = Math.max(1, Math.min(page - 2, Math.max(1, totalPages - 4)));
                      const end = Math.min(totalPages, start + 4);
                      for (let p = start; p <= end; p++) buttons.push(p);
                      return buttons.map((p) => (
                        <button
                          key={p}
                          onClick={() => {
                            setPage(p);
                            loadUsers(p, query);
                          }}
                          aria-current={p === page ? "page" : undefined}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 ${p === page
                            ? "z-10 bg-primary-50 text-primary-600"
                            : "bg-white text-gray-900 hover:bg-gray-50"
                            } focus:z-20`}
                        >
                          {p}
                        </button>
                      ));
                    })()}
                    <button
                      onClick={() => {
                        const next = Math.min(totalPages, page + 1);
                        setPage(next);
                        loadUsers(next, query);
                      }}
                      disabled={page === totalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20"
                    >
                      <span className="sr-only">Next</span>
                      {/* chevron right */}
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.22 4.78a.75.75 0 011.06 0l4.5 4.5a.75.75 0 010 1.06l-4.5 4.5a.75.75 0 11-1.06-1.06L11.19 10 7.22 6.03a.75.75 0 010-1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>




      {showCreateUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Create New User
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Username
                  </label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => {
                      setNewUser({ ...newUser, username: e.target.value });
                      if (errors.username) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.username;
                          return next;
                        });
                      }
                    }}
                    className={`mt-1 block w-full border ${errors.username ? "border-red-500" : "border-gray-300"
                      } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                  />
                  {errors.username && (
                    <p className="mt-1 text-xs text-red-600">{errors.username}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => {
                      setNewUser({ ...newUser, name: e.target.value });
                      if (errors.name) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.name;
                          return next;
                        });
                      }
                    }}
                    className={`mt-1 block w-full border ${errors.name ? "border-red-500" : "border-gray-300"
                      } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => {
                      setNewUser({ ...newUser, email: e.target.value });
                      if (errors.email) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.email;
                          return next;
                        });
                      }
                    }}
                    className={`mt-1 block w-full border ${errors.email ? "border-red-500" : "border-gray-300"
                      } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="relative mt-1">
                    <input
                      type={showCreatePassword ? "text" : "password"}
                      value={newUser.password}
                      onChange={(e) => {
                        setNewUser({ ...newUser, password: e.target.value });
                        if (errors.password) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.password;
                            return next;
                          });
                        }
                      }}
                      className={`pl-3 pr-10 block w-full border ${errors.password ? "border-red-500" : "border-gray-300"
                        } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      onClick={() => setShowCreatePassword(!showCreatePassword)}
                      aria-label={showCreatePassword ? "Hide password" : "Show password"}
                    >
                      {showCreatePassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.password}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        role: e.target.value as "admin" | "staff",
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateUser(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={isSaving || Object.keys(errors).length > 0}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSaving ? "Creating..." : "Create User"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditUser && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit User
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => {
                      setEditForm({ ...editForm, name: e.target.value });
                      if (editErrors.name) {
                        setEditErrors((prev) => {
                          const next = { ...prev };
                          delete next.name;
                          return next;
                        });
                      }
                    }}
                    className={`mt-1 block w-full border ${editErrors.name ? "border-red-500" : "border-gray-300"
                      } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                  />
                  {editErrors.name && (
                    <p className="mt-1 text-xs text-red-600">{editErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as "admin" | "staff" })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Password</label>
                  <div className="relative mt-1">
                    <input
                      type={editForm as any && (editForm as any)._showNewPwd ? "text" : "password"}
                      value={editForm.newPassword}
                      onChange={(e) => {
                        setEditForm({ ...editForm, newPassword: e.target.value });
                        if (editErrors.newPassword) {
                          setEditErrors((prev) => {
                            const next = { ...prev };
                            delete next.newPassword;
                            return next;
                          });
                        }
                      }}
                      className={`pl-3 pr-10 block w-full border ${editErrors.newPassword ? "border-red-500" : "border-gray-300"
                        } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      onClick={() =>
                        setEditForm({
                          ...editForm,
                          ...(editForm as any)._showNewPwd
                            ? { ...(editForm as any), _showNewPwd: false }
                            : { ...(editForm as any), _showNewPwd: true },
                        } as any)
                      }
                    >
                      {(editForm as any)._showNewPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {editErrors.newPassword && (
                    <p className="mt-1 text-xs text-red-600">{editErrors.newPassword}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                  <div className="relative mt-1">
                    <input
                      type={editForm as any && (editForm as any)._showConfirmPwd ? "text" : "password"}
                      value={editForm.confirmPassword}
                      onChange={(e) => {
                        setEditForm({ ...editForm, confirmPassword: e.target.value });
                        if (editErrors.confirmPassword) {
                          setEditErrors((prev) => {
                            const next = { ...prev };
                            delete next.confirmPassword;
                            return next;
                          });
                        }
                      }}
                      className={`pl-3 pr-10 block w-full border ${editErrors.confirmPassword ? "border-red-500" : "border-gray-300"
                        } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      onClick={() =>
                        setEditForm({
                          ...editForm,
                          ...(editForm as any)._showConfirmPwd
                            ? { ...(editForm as any), _showConfirmPwd: false }
                            : { ...(editForm as any), _showConfirmPwd: true },
                        } as any)
                      }
                    >
                      {(editForm as any)._showConfirmPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {editErrors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">{editErrors.confirmPassword}</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditUser(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={isSaving}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete User</h3>
              <p className="text-sm text-gray-700">
                Are you sure you want to delete {selectedUser.username}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isSaving}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
